import {
  faExclamationCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropertiesStore } from '@hortplus/properties-react';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip
} from 'react-bootstrap';
import Select from 'react-select';
import URI from 'urijs';
import * as yup from 'yup';
import { calcAccumulationThreshold } from '../../helpers/TrappingUtils';
import { useStores } from '../../stores/useStores';

const Option = (props) => {
  const [hovering, setHovering] = useState(false);

  return (
    <Row
      className={`border rounded p-3 m-3${
        props.selected ? ' border-primary' : ''
      }${hovering ? ' shadow' : ''}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {props.children}
    </Row>
  );
};

function TrappingMonitoringReport(props) {
  const { traplogStore } = useStores();

  const [propertyGroups, setPropertyGroups] = useState([]);
  const [loadingPropertyGroups, setLoadingPropertyGroups] = useState(false);

  const [selectedProperties, setSelectedProperties] = useState([]);

  // This state is used to only reload results when the form is submitted
  const [selectedInsectGroup, setSelectedInsectGroup] = useState();

  const [loadingTrappingResults, setLoadingTrappingResults] = useState(false);
  const [trappingResults, setTrappingResults] = useState({});

  /*
   * Load insect groups and property groups
   */
  useEffect(() => {
    formik.setFieldValue('propertyGroups', []);
    formik.handleSubmit();

    traplogStore.loadInsectGroups(props.bearerToken, props.refreshBearerToken);

    setLoadingPropertyGroups(true);
    fetch(`${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${props.bearerToken}`,
        SeasonalDatabase: props.selectedDatabase
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) traplogStore.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          setPropertyGroups(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        setPropertyGroups([]);
      })
      .finally(() => {
        setLoadingPropertyGroups(false);
      });
  }, [props.selectedDatabase, props.bearerToken]);

  /*
   * Load trapping results
   */
  useEffect(() => {
    if (selectedInsectGroup && selectedProperties.length > 0) {
      setLoadingTrappingResults(true);

      const url = new URI(
        `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-results`
      );

      url.addQuery('insect_group_id', selectedInsectGroup.id);

      selectedProperties.forEach((property) => {
        property.trapping_zones.forEach((trappingZone) => {
          url.addQuery('trapping_zones[]', trappingZone.id);
        });
      });

      fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${props.bearerToken}`,
          SeasonalDatabase: props.selectedDatabase
        }
      })
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) traplogStore.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setTrappingResults(data);
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
        })
        .finally(() => {
          setLoadingTrappingResults(false);
        });
    }
  }, [
    props.selectedDatabase,
    props.bearerToken,
    selectedProperties,
    selectedInsectGroup
  ]);

  const formik = useFormik({
    initialValues: {
      selector: 'trapping-runs',
      propertyGroups: [],
      visitedDate: moment().format('YYYY-MM-D'),
      insectGroup: null
    },
    validationSchema: yup.object().shape({
      insectGroup: yup.object().required()
    }),
    onSubmit: async (values, actions) => {
      setSelectedProperties([]);
      setTrappingResults({});
      setSelectedInsectGroup(values.insectGroup);
      if (values.selector === 'trapping-runs') {
        const properties = [];
        await Promise.all(
          values.propertyGroups.map((propertyGroup) =>
            fetch(
              `${process.env.REACT_APP_TRAPLOG_API_URL}api/property-groups/${propertyGroup.value}?with[]=properties.trappingZones.traps&with[]=properties.trappingZones.matingDisruptionTie`,
              {
                headers: {
                  Authorization: `Bearer ${props.bearerToken}`,
                  Accept: 'application/json',
                  SeasonalDatabase: props.selectedDatabase
                }
              }
            )
              .then((res) => {
                if (res.ok) return res.json();
                else if (res.status === 401) traplogStore.refreshBearerToken();
                else throw Error(res.statusText);
              })
              .then((data) => {
                data.properties.map((property) => {
                  if (!properties.find((p) => p.id === property.id))
                    properties.push(property);
                });
              })
              .catch((err) => {
                if (process.env.NODE_ENV === 'development') console.error(err);
              })
          )
        );

        setSelectedProperties(properties);
      } else if (values.selector === 'visited-date' && values.insectGroup) {
        fetch(
          `${process.env.REACT_APP_TRAPLOG_API_URL}api/properties/monitored-at?visited_date=${values.visitedDate}&insect_group_id=${values.insectGroup.id}`,
          {
            headers: {
              Authorization: `Bearer ${props.bearerToken}`,
              Accept: 'application/json',
              SeasonalDatabase: props.selectedDatabase
            }
          }
        )
          .then((res) => {
            if (res.ok) return res.json();
            else if (res.status === 401) traplogStore.refreshBearerToken();
            else throw Error(res.statusText);
          })
          .then((data) => {
            setSelectedProperties(data);
          })
          .catch((err) => {
            if (process.env.NODE_ENV === 'development') console.error(err);
          });
      }
    }
  });

  return (
    <Fragment>
      <Form onSubmit={formik.handleSubmit}>
        <Row className='justify-content-center'>
          <Col sm={10} md={8} lg={6}>
            <Option selected={formik.values.selector === 'trapping-runs'}>
              <Col>
                <Row>
                  <Col>
                    <Form.Check
                      type='radio'
                      name='selector'
                      value='trapping-runs'
                      id='trapping-runs'
                      label='Trapping Runs'
                      onChange={formik.handleChange}
                      checked={formik.values.selector === 'trapping-runs'}
                      custom
                    />
                  </Col>
                </Row>
                {formik.values.selector === 'trapping-runs' ? (
                  <Row className='mt-2'>
                    <Col>
                      <Select
                        options={[
                          {
                            label: 'Trapping Runs',
                            options: propertyGroups
                              .filter(
                                (propertyGroup) =>
                                  !propertyGroup.deleted_at &&
                                  propertyGroup.group_type === 'trapping_run'
                              )
                              .map((propertyGroup) => {
                                return {
                                  value: propertyGroup.id,
                                  label: propertyGroup.name
                                };
                              })
                          },
                          {
                            label: 'Property Groups',
                            options: propertyGroups
                              .filter(
                                (propertyGroup) =>
                                  !propertyGroup.deleted_at &&
                                  propertyGroup.group_type === 'manual'
                              )
                              .map((propertyGroup) => {
                                return {
                                  value: propertyGroup.id,
                                  label: propertyGroup.name
                                };
                              })
                          }
                        ]}
                        isLoading={loadingPropertyGroups}
                        onChange={(options) => {
                          formik.setFieldValue(
                            'propertyGroups',
                            options ? options.map((option) => option) : []
                          );
                        }}
                        value={formik.values.propertyGroups}
                        isMulti
                      />
                    </Col>
                  </Row>
                ) : null}
              </Col>
            </Option>
            <Option selected={formik.values.selector === 'visited-date'}>
              <Col>
                <Row>
                  <Col>
                    <Form.Check
                      type='radio'
                      name='selector'
                      value='visited-date'
                      id='visited-date'
                      label='Visited Date'
                      onChange={formik.handleChange}
                      checked={formik.values.selector === 'visited-date'}
                      custom
                      style={{
                        zIndex: 0
                      }}
                    />
                  </Col>
                </Row>
                {formik.values.selector === 'visited-date' ? (
                  <Row className='mt-2'>
                    <Col>
                      <Form.Control
                        type='date'
                        name='visitedDate'
                        if='visitedDate'
                        value={formik.values.visitedDate}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                ) : null}
              </Col>
            </Option>
            <div className='mx-3'>
              <Select
                className='mt-3'
                placeholder='Select an insect'
                isLoading={traplogStore.loadingInsectGroups}
                options={traplogStore.insectGroups.map((insectGroup) => {
                  return {
                    value: insectGroup.id,
                    label: insectGroup.name,
                    custom: insectGroup
                  };
                })}
                onChange={(option) => {
                  formik.setFieldValue('insectGroup', option.custom);
                }}
              />
            </div>
            <Row className='mt-3'>
              <Col className='text-center'>
                <Button variant='outline-primary' type='submit'>
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      {!selectedInsectGroup ||
      selectedProperties.length < 1 ? null : loadingPropertyGroups ||
        loadingTrappingResults ||
        traplogStore.loadingInsectGroups ? (
        <Spinner variant='primary' animation='border' />
      ) : (
        selectedProperties.map((property) => (
          <Row key={property.id} className='my-3'>
            <Col>
              <h5>
                [{property.identifier}] {property.name}
              </h5>
              <Table size='sm' bordered hover>
                <thead>
                  <tr className='thead-light'>
                    <th>Production Site</th>
                    <th>Last Monitored</th>
                    <th>Last Threshold</th>
                    <th>Last Spray</th>
                    <th>Threshold Met?</th>
                  </tr>
                </thead>
                <tbody>
                  {property.trapping_zones.map((trappingZone) => {
                    const trappingZoneResults =
                      trappingResults[trappingZone.id];

                    let lastMonitored = null;
                    let latestSpray = null;
                    let latestThreshold = null;

                    // Loop starting from the latest results
                    if (trappingZoneResults) {
                      for (
                        let i = trappingZoneResults.results.length - 1;
                        i >= 0;
                        i--
                      ) {
                        const result = trappingZoneResults.results[i];

                        if (!lastMonitored) {
                          if (result.trap_monitoring_records.length > 0)
                            lastMonitored = result.date;
                        }

                        // Find the latest sprayed date
                        if (!latestSpray) {
                          if (result.trapping_zone_sprays.length > 0)
                            latestSpray = result.date;
                        }

                        // Find the latest threshold date
                        if (!latestThreshold && !result.sprayed) {
                          const trapIds = Object.keys(result.traps);
                          const amounts = [];

                          for (let j = 0; j < trapIds.length; j++) {
                            const trap = result.traps[trapIds[j]];

                            if (
                              trap.amount >=
                              selectedInsectGroup.per_trap_catch_threshold
                            ) {
                              latestThreshold = result.date;
                              break;
                            } else if (
                              trap.amount !== null &&
                              trap.accumulation >=
                                calcAccumulationThreshold(
                                  selectedInsectGroup,
                                  result.date
                                )
                            ) {
                              latestThreshold = result.date;
                              break;
                            } else {
                              amounts.push(trap.amount);
                            }
                          }

                          if (!latestThreshold) {
                            if (
                              amounts.length > 0 &&
                              amounts.reduce(
                                (accumulator, currentValue) =>
                                  accumulator + currentValue
                              ) /
                                amounts.length >=
                                selectedInsectGroup.average_threshold
                            )
                              latestThreshold = result.date;
                          }
                        }

                        // Break the loop if both the latest spray and threshold have been found
                        if (lastMonitored && latestSpray && latestThreshold)
                          break;
                      }
                    }

                    const lastMonitoredMoment = lastMonitored
                      ? moment(lastMonitored)
                      : null;
                    const latestSprayMoment = latestSpray
                      ? moment(latestSpray)
                      : null;
                    const latestThresholdMoment = latestThreshold
                      ? moment(latestThreshold)
                      : null;

                    return (
                      <tr key={trappingZone.id}>
                        <td>
                          {trappingZone.name}
                          {trappingZone.cmsm ? ' (CMSM)' : ''}
                          {trappingZone.mating_disruption_tie ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip>
                                  {trappingZone.mating_disruption_tie.name}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                className='ml-2'
                                size='xs'
                              />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                        <td>
                          {lastMonitoredMoment
                            ? lastMonitoredMoment.format(
                                'DD/MM/YYYY [(Week] w[)]'
                              )
                            : '-'}
                        </td>
                        <td>
                          {latestThresholdMoment
                            ? latestThresholdMoment.format(
                                'DD/MM/YYYY [(Week] w[)]'
                              )
                            : '-'}
                        </td>
                        <td>
                          {latestSprayMoment
                            ? latestSprayMoment.format(
                                'DD/MM/YYYY [(Week] w[)]'
                              )
                            : '-'}
                        </td>
                        <td>
                          {trappingZoneResults &&
                          (trappingZoneResults.overview.status ===
                            'pre_trap_catch_threshold_met' ||
                            trappingZoneResults.overview.status ===
                              'accumulation_threshold_met' ||
                            trappingZoneResults.overview.status ===
                              'average_threshold_met') ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>A threshold is met</Tooltip>}
                            >
                              <FontAwesomeIcon
                                className='text-danger'
                                icon={faExclamationCircle}
                              />
                            </OverlayTrigger>
                          ) : latestThresholdMoment ? (
                            (latestSprayMoment &&
                              latestSprayMoment.isBefore(
                                latestThresholdMoment
                              )) ||
                            !latestSprayMoment ? (
                              <OverlayTrigger
                                placement='top'
                                overlay={
                                  <Tooltip>
                                    A previous threshold was not actioned
                                  </Tooltip>
                                }
                              >
                                <FontAwesomeIcon
                                  className='text-danger'
                                  icon={faExclamationCircle}
                                />
                              </OverlayTrigger>
                            ) : null
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Col>
          </Row>
        ))
      )}
    </Fragment>
  );
}

TrappingMonitoringReport.propTypes = {
  selectedDatabase: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired
};

export default observer(TrappingMonitoringReport);
